import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type * as SVGO from "svgo";

import { optimize } from "svgo";

const generateUniquePrefix = () =>
  `${Math.random().toString(36).substring(2, 10)}-${Date.now().toString(36)}`;

export const optimizeSvg = (
  userConfig: ZeytalCLIPureConfig,
  assetName: string,
  assetData: Buffer,
): Buffer => {
  const svgOptimizerConfig = userConfig.assets.optimize.svg ?? {};

  const [svgFilename] = assetName
    .split("/")
    .filter((part) => part.includes(".svg"));

  // If the filename is weird and doesn't contain .svg, we generate a random prefix to avoid id conflicts
  const uniquePrefix = svgFilename
    ? svgFilename.replace(".svg", "")
    : generateUniquePrefix();

  // Allowed config keys
  const { multipass, floatPrecision, plugins } = svgOptimizerConfig;

  const typedPlugins = plugins as SVGO.PluginConfig[];

  const filteredPlugins = typedPlugins?.filter(
    (plugin: string | { name: string }) => {
      if (
        (typeof plugin === "string" && plugin === "cleanupIds") ||
        (typeof plugin !== "string" && plugin.name === "cleanupIds")
      ) {
        return false;
      } else if (
        (typeof plugin === "string" && plugin === "prefixIds") ||
        (typeof plugin !== "string" && plugin.name === "prefixIds")
      ) {
        return false;
      }
      return true;
    },
  );

  const { data: optimizedData } = optimize(assetData.toString("utf-8"), {
    multipass,
    floatPrecision,
    plugins: [
      // User's svgo plugins config
      ...(filteredPlugins ?? []),
      // Opinionated config that cannot be overriden to force unique ids generation
      {
        name: "cleanupIds",
        params: {
          remove: true,
          minify: true,
        },
      },
      {
        name: "prefixIds",
        params: {
          delim: "-",
          prefix: uniquePrefix,
          prefixIds: true,
          prefixClassNames: false,
        },
      },
    ],
  });

  return Buffer.from(optimizedData, "utf-8");
};
